import { type ApiPlannedContainerAction, EnumPlannedActionSource } from '@Model';
import {
    type ComingCard,
    type OtherOrder,
    type OwnPlacementCard,
    type PlannedElsewhere,
    type StockCard,
} from './siteCards';
import { otherOrderTakesComing, type OwnOrder } from './siteMatching';

/** The cards of the site the rules look at, with what other orders plan for each stock card by its key. */
export interface SiteCardsToCheck {
    stockCards: readonly StockCard[];
    ownCards: readonly OwnPlacementCard[];
    comingCards: readonly ComingCard[];
    elsewhere: ReadonlyMap<string, PlannedElsewhere>;
}

/** What the sidebar asks about as a possible double planning; a hint only, it blocks nothing. */
export interface DoublePlannings {
    /** The keys of the stock cards whose containers this order and others take away more often than they stand there. */
    stock: ReadonlySet<string>;
    /** By own placement card, the other orders whose placements of the same type this order does not take back. */
    placements: ReadonlyMap<string, OtherOrder[]>;
}

/** The double plannings at the site, each rule applied to the cards it is about. */
// @ai-generated 2026-10-22T12:22:16+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5
export function findDoublePlannings(site: SiteCardsToCheck): DoublePlannings {
    return {
        stock: new Set(
            site.stockCards
                .filter((card) => {
                    const elsewhere = site.elsewhere.get(card.key);
                    return !!elsewhere && isTakenAwayTwice(card, elsewhere);
                })
                .map((card) => card.key),
        ),
        placements: placedTwice(site.ownCards, site.comingCards),
    };
}

/** The keys of the stock and coming cards whose containers this order and other orders together all take away. */
// @ai-generated 2026-10-28T14:13:11+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5
export function findDepartures(
    site: SiteCardsToCheck,
    planned: readonly ApiPlannedContainerAction[],
    order: OwnOrder,
): Set<string> {
    const leavingStock = site.stockCards.filter(
        (card) => card.removedInPresentOrder + (site.elsewhere.get(card.key)?.removedCount ?? 0) >= card.group.count,
    );
    const leavingComing = site.comingCards.filter((card) => {
        const removedElsewhere = planned
            .filter((taking) => otherOrderTakesComing(taking, card.planned, order))
            .reduce((sum, taking) => sum + taking.count, 0);

        return removedElsewhere > 0 && card.removedInPresentOrder + removedElsewhere >= card.count;
    });

    return new Set([...leavingStock, ...leavingComing].map((card) => card.key));
}

/** Whether this order takes a card's containers away and, with the other orders, takes more than stand there. */
// @ai-generated 2026-10-21T12:37:24+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5
export function isTakenAwayTwice(card: StockCard, elsewhere: PlannedElsewhere): boolean {
    return card.removedInPresentOrder > 0 && card.removedInPresentOrder + elsewhere.removedCount > card.group.count;
}

/**
 * By own placement card, the other orders still placing the same container type once this order has taken back what
 * it picks up or swaps out of theirs; only where this order places more of the type than it takes back. The delivery
 * of another order's swap that takes away a container of the same type replaces it, so it never counts as placed twice.
 */
// @ai-generated 2026-10-22T12:22:16+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5
export function placedTwice(
    ownCards: readonly OwnPlacementCard[],
    comingCards: readonly ComingCard[],
): Map<string, OtherOrder[]> {
    const result = new Map<string, OtherOrder[]>();
    const typeIds = new Set(ownCards.map((card) => card.action.container_type.foreign_key));

    for (const typeId of typeIds) {
        const own = ownCards.filter((card) => card.action.container_type.foreign_key === typeId);
        const others = comingCards.filter(
            (card) =>
                card.planned.source === EnumPlannedActionSource.Order &&
                card.planned.container_type.foreign_key === typeId,
        );
        const added = own.reduce((sum, card) => sum + card.count, 0);
        const takenBack = others.reduce((sum, card) => sum + card.removedInPresentOrder, 0);
        const leftOver = ordersOf(
            others.filter((card) => !card.replacesSameType && card.removedInPresentOrder < card.count),
        );

        if (added > takenBack && leftOver.length > 0) {
            own.forEach((card) => result.set(card.key, leftOver));
        }
    }

    return result;
}

// @ai-generated 2026-10-22T12:22:16+00:00 by malte.ulfers@tngtech.com using Claude Opus 5.5
function ordersOf(cards: readonly ComingCard[]): OtherOrder[] {
    const orders = new Map<string, OtherOrder>();
    for (const { planned } of cards) {
        orders.set(planned.source_id, { orderId: planned.source_id, number: planned.source_number });
    }

    return [...orders.values()];
}
